import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, TrendingUp, CheckCircle, Rocket, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const PROCESS_STEPS = [
  {
    id: 1,
    icon: Users,
    color: '#f59e0b',
    titleES: 'Workshops Participativos',
    titleEN: 'Participatory Workshops',
    descES: '+40 empleados de 4 áreas de negocio identifican necesidades y oportunidades desde el terreno.',
    descEN: '+40 employees from 4 business areas identify needs and opportunities from the field.',
  },
  {
    id: 2,
    icon: TrendingUp,
    color: '#3b82f6',
    titleES: 'Análisis y Priorización',
    titleEN: 'Analysis & Prioritization',
    descES: '20 retos analizados mediante matriz Impacto vs. Dificultad. Filtrado a TOP 8 con mayor potencial estratégico.',
    descEN: '20 challenges analyzed through Impact vs. Difficulty matrix. Filtered to TOP 8 with highest strategic potential.',
  },
  {
    id: 3,
    icon: CheckCircle,
    color: '#8b5cf6',
    titleES: 'Comité de Innovación',
    titleEN: 'Innovation Committee',
    descES: 'Directores validan, priorizan TOP 3 y TOP 5, asignan sponsors y seleccionan vehículos de innovación.',
    descEN: 'Directors validate, prioritize TOP 3 and TOP 5, assign sponsors and select innovation vehicles.',
  },
  {
    id: 4,
    icon: Rocket,
    color: '#8DC21F',
    titleES: 'Activación H2 2026',
    titleEN: 'H2 2026 Activation',
    descES: 'Ejecución mediante scouting, pilotos, ayudas públicas o innovación abierta según cada reto.',
    descEN: 'Execution through scouting, pilots, public funding or open innovation according to each challenge.',
  },
];

function ProcessStep({ step, index, inView, isLast, lang }) {
  const Icon = step.icon;
  const title = lang === 'es' ? step.titleES : step.titleEN;
  const desc = lang === 'es' ? step.descES : step.descEN;

  return (
    <div className="relative flex flex-col items-center">
      {/* Step card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.6, delay: index * 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full"
      >
        <div
          className="group relative rounded-2xl p-6 transition-all duration-300 hover:scale-105"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Step number badge */}
          <div 
            className="absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{
              background: step.color,
              color: '#000',
              boxShadow: `0 4px 12px ${step.color}40`,
            }}
          >
            {step.id}
          </div>

          {/* Icon */}
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
            style={{
              background: `${step.color}15`,
              border: `1px solid ${step.color}30`,
            }}
          >
            <Icon size={24} style={{ color: step.color }} />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-white mb-3 leading-tight">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-white/60 leading-relaxed">
            {desc}
          </p>

          {/* Hover effect border */}
          <div 
            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{ 
              border: `1px solid ${step.color}40`,
            }}
          />
        </div>
      </motion.div>

      {/* Arrow connector (not on last item) */}
      {!isLast && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
          className="hidden md:block absolute top-1/2 -right-6 z-0"
        >
          <ArrowRight size={24} style={{ color: step.color, opacity: 0.4 }} />
        </motion.div>
      )}
    </div>
  );
}

export default function InnovationProcessSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { lang } = useLanguage();

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(3,3,8,0.98) 50%, #000 100%)',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ 
          background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(59,130,246,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="v-container relative z-10">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag" style={{ 
            background: 'rgba(59,130,246,0.1)', 
            color: '#3b82f6',
            border: '1px solid rgba(59,130,246,0.25)' 
          }}>
            {lang === 'es' ? 'NUESTRO PROCESO' : 'OUR PROCESS'}
          </span>
        </motion.div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
              {lang === 'es' ? (
                <>
                  De <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>idea colectiva</em>{' '}
                  a{' '}
                  <span style={{ color: '#3b82f6' }}>ejecución estratégica</span>
                </>
              ) : (
                <>
                  From <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>collective idea</em>{' '}
                  to{' '}
                  <span style={{ color: '#3b82f6' }}>strategic execution</span>
                </>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-base text-white/60 leading-relaxed mb-4">
              {lang === 'es'
                ? 'Un proceso estructurado y transparente que convierte la inteligencia colectiva de Valoriza en proyectos de innovación con impacto real. Desde el terreno hasta el comité directivo, cada voz cuenta.'
                : 'A structured and transparent process that converts Valoriza\'s collective intelligence into innovation projects with real impact. From the field to the executive committee, every voice counts.'}
            </p>
            <div className="flex items-center gap-2 text-sm text-white/50">
              <Sparkles size={16} style={{ color: '#3b82f6' }} />
              <span>
                {lang === 'es' 
                  ? 'Metodología validada por el Comité de Innovación' 
                  : 'Methodology validated by the Innovation Committee'}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Process steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {PROCESS_STEPS.map((step, index) => (
            <ProcessStep
              key={step.id}
              step={step}
              index={index}
              inView={inView}
              isLast={index === PROCESS_STEPS.length - 1}
              lang={lang}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <div 
            className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl"
            style={{
              background: 'rgba(59,130,246,0.05)',
              border: '1px solid rgba(59,130,246,0.15)',
            }}
          >
            <CheckCircle size={20} style={{ color: '#3b82f6' }} />
            <span className="text-sm text-white/70">
              {lang === 'es'
                ? 'Proceso en curso para H2 2026 - Activación prevista Q3 2026'
                : 'Ongoing process for H2 2026 - Activation planned Q3 2026'}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
